if (!customElements.get('finch-new-arrivals-slider')) {
  class FinchNewArrivalsSlider extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.tabs = Array.from(this.querySelectorAll('[data-fnas-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-fnas-panel]'));
      this.tabList = this.querySelector('[data-fnas-tabs]');
      this.scrollStep = Number(this.dataset.scrollStep) || 1;
      this.itemLabel = this.dataset.itemLabel || 'Item';
      this.ofLabel = this.dataset.ofLabel || 'of';
      this.panelStates = new Map();

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (event) => this.activateTab(event.currentTarget, false), {
          signal: this.signal,
        });
        tab.addEventListener('keydown', (event) => this.handleTabKeydown(event), { signal: this.signal });
      });

      this.panels.forEach((panel) => this.setupPanel(panel));
      this.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), {
        signal: this.signal,
      });

      this.dataset.enhanced = 'true';
      if (this.tabs.length > 1 && this.tabList) this.tabList.hidden = false;

      const selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      if (selectedTab) {
        this.activateTab(selectedTab, false);
      } else {
        this.panels.forEach((panel, index) => {
          panel.hidden = index > 0;
          if (index === 0) this.scheduleUpdate(this.panelStates.get(panel));
        });
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;

      this.panelStates?.forEach((state) => {
        if (state.updateFrame) window.cancelAnimationFrame(state.updateFrame);
        if (state.announceTimer) window.clearTimeout(state.announceTimer);
      });
      this.panelStates?.clear();
      delete this.dataset.enhanced;
    }

    setupPanel(panel) {
      const track = panel.querySelector('[data-fnas-track]');
      if (!track) return;

      const state = {
        panel,
        track,
        slides: Array.from(track.querySelectorAll('[data-fnas-slide]')),
        previousButton: panel.querySelector('[data-fnas-previous]'),
        nextButton: panel.querySelector('[data-fnas-next]'),
        progressBar: panel.querySelector('[data-fnas-progress-bar]'),
        controls: panel.querySelector('[data-fnas-controls]'),
        status: panel.querySelector('[data-fnas-status]'),
        updateFrame: null,
        announceTimer: null,
        announcementPending: false,
      };
      this.panelStates.set(panel, state);

      if (state.controls) state.controls.hidden = false;
      track.addEventListener(
        'scroll',
        () => {
          this.scheduleUpdate(state);
          if (state.announcementPending) this.scheduleAnnouncement(state);
        },
        { passive: true, signal: this.signal }
      );
      if ('onscrollend' in track) {
        track.addEventListener('scrollend', () => this.flushAnnouncement(state), { signal: this.signal });
      }
      track.addEventListener('keydown', (event) => this.handleTrackKeydown(event, state), { signal: this.signal });
      state.previousButton?.addEventListener('click', () => this.move(state, -1, true), { signal: this.signal });
      state.nextButton?.addEventListener('click', () => this.move(state, 1, true), { signal: this.signal });

      if ('ResizeObserver' in window) {
        if (!this.resizeObserver) this.resizeObserver = new ResizeObserver(() => this.updateVisiblePanel());
        this.resizeObserver.observe(track);
      } else {
        window.addEventListener('resize', () => this.scheduleUpdate(state), { signal: this.signal });
      }

      this.scheduleUpdate(state);
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      const isRtl = getComputedStyle(this).direction === 'rtl';
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + (isRtl ? -1 : 1) + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex + (isRtl ? 1 : -1) + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.activateTab(this.tabs[nextIndex], true);
    }

    activateTab(activeTab, moveFocus) {
      if (!activeTab) return;

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      let activePanel = null;
      this.panels.forEach((panel) => {
        const isActive = panel.id === activeTab.dataset.panelId;
        panel.hidden = !isActive;
        if (isActive) activePanel = panel;
      });

      if (moveFocus) {
        activeTab.scrollIntoView({
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
        activeTab.focus();
      }

      if (activePanel) {
        window.requestAnimationFrame(() => this.scheduleUpdate(this.panelStates.get(activePanel)));
      }
    }

    handleBlockSelect(event) {
      const panel = event.target.closest?.('[data-fnas-panel]');
      if (!panel) return;

      const tab = this.tabs.find((candidate) => candidate.dataset.panelId === panel.id);
      if (tab) this.activateTab(tab, false);
    }

    handleTrackKeydown(event, state) {
      if (event.target !== state.track) return;

      const isRtl = getComputedStyle(state.track).direction === 'rtl';
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(state, isRtl ? -1 : 1, true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(state, isRtl ? 1 : -1, true);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.scrollToPosition(state, 0, true);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.scrollToPosition(state, this.getMaxScroll(state), true);
      }
    }

    updateVisiblePanel() {
      const visiblePanel = this.panels.find((panel) => !panel.hidden);
      if (visiblePanel) this.scheduleUpdate(this.panelStates.get(visiblePanel));
    }

    scheduleUpdate(state) {
      if (!state || state.updateFrame) return;

      state.updateFrame = window.requestAnimationFrame(() => {
        state.updateFrame = null;
        this.update(state);
      });
    }

    update(state) {
      const maxScroll = this.getMaxScroll(state);
      const position = this.getPosition(state);
      const isStatic = maxScroll <= 1 || state.slides.length <= 1;

      state.panel.dataset.static = isStatic ? 'true' : 'false';
      this.setButtonState(state.previousButton, isStatic || position <= 1);
      this.setButtonState(state.nextButton, isStatic || position >= maxScroll - 1);

      if (state.progressBar) {
        const progress = isStatic ? 100 : 20 + (position / maxScroll) * 80;
        state.progressBar.style.inlineSize = `${progress}%`;
        state.progressBar.style.marginInlineStart = '0%';
      }
    }

    setButtonState(button, disabled) {
      if (!button) return;
      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    getMaxScroll(state) {
      return Math.max(0, state.track.scrollWidth - state.track.clientWidth);
    }

    getPosition(state) {
      return Math.min(this.getMaxScroll(state), Math.max(0, Math.abs(state.track.scrollLeft)));
    }

    getStepSize(state) {
      const firstSlide = state.slides[0];
      if (!firstSlide) return state.track.clientWidth;

      const styles = window.getComputedStyle(state.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return (firstSlide.getBoundingClientRect().width + gap) * this.scrollStep;
    }

    move(state, direction, announce) {
      const button = direction < 0 ? state.previousButton : state.nextButton;
      if (button?.disabled) return;

      const isRtl = getComputedStyle(state.track).direction === 'rtl';
      const physicalDirection = isRtl ? direction * -1 : direction;
      state.track.scrollBy({
        left: this.getStepSize(state) * physicalDirection,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
      if (announce) this.queueAnnouncement(state);
    }

    scrollToPosition(state, position, announce) {
      const isRtl = getComputedStyle(state.track).direction === 'rtl';
      state.track.scrollTo({
        left: isRtl ? position * -1 : position,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
      if (announce) this.queueAnnouncement(state);
    }

    queueAnnouncement(state) {
      state.announcementPending = true;
      this.scheduleAnnouncement(state);
    }

    scheduleAnnouncement(state) {
      if (state.announceTimer) window.clearTimeout(state.announceTimer);
      state.announceTimer = window.setTimeout(() => {
        this.flushAnnouncement(state);
      }, this.reducedMotion.matches ? 50 : 180);
    }

    flushAnnouncement(state) {
      if (!state.announcementPending) return;
      if (state.announceTimer) window.clearTimeout(state.announceTimer);
      state.announceTimer = null;
      state.announcementPending = false;
      this.update(state);
      this.announce(state);
    }

    announce(state) {
      if (!state.status || state.slides.length === 0) return;

      const stepSize = Math.max(1, this.getStepSize(state) / this.scrollStep);
      const firstVisibleIndex = Math.min(
        state.slides.length,
        Math.max(1, Math.round(this.getPosition(state) / stepSize) + 1)
      );
      state.status.textContent = `${this.itemLabel} ${firstVisibleIndex} ${this.ofLabel} ${state.slides.length}`;
    }
  }

  customElements.define('finch-new-arrivals-slider', FinchNewArrivalsSlider);
}
